import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

const TERMS = `CocoWash Service Center Partner Agreement

Last updated: July 2026

1. WHAT COCOWASH DOES
CocoWash is a vehicle care marketplace based in Montgomery County, Maryland. We connect customers with trusted local service centers by picking up customers vehicles, bringing them to your facility, and returning them after service is complete. CocoWash handles all customer communication, payment processing, and logistics.

2. WHAT YOU AGREE TO DO
As a Service Center Partner you agree to:
- Provide professional, high-quality vehicle care services at all times
- Honor the services and pricing listed on your CocoWash profile
- Complete services within the agreed timeframe
- Treat every CocoWash customer vehicle with the same care you would give your best walk-in customer
- Notify CocoWash promptly if you are unable to fulfill a scheduled job
- Maintain all required business licenses, certifications, and permits
- Maintain comprehensive garage liability insurance covering all vehicles in your care, custody, and control
- Maintain garage keepers insurance to cover customer vehicles against damage, theft, or loss while on your premises
- Maintain all other required business insurance including general liability and workers compensation as required by the State of Maryland
- Provide proof of current insurance coverage to CocoWash upon request

3. YOUR FREE TRIAL - 90 DAYS AT NO COST
When you join CocoWash you receive a 90-day free trial. During this period:
- CocoWash will send you customers at absolutely no charge
- You keep 100% of every payment for jobs CocoWash brings you
- No credit card required to start
- No hidden fees during the trial period

4. REVENUE SHARE AFTER YOUR FREE TRIAL
After your 90-day free trial ends, CocoWash will retain 15% of the job value for every customer we send to your facility. You keep 100% of all walk-in customers and jobs that did not come through CocoWash. CocoWash only earns when we bring you business. There is no monthly subscription fee ever.

5. PAYMENTS - COCOWASH ISSUED PAYMENT CARD
When a CocoWash driver delivers a vehicle to your facility, they will present a CocoWash Issued Payment Card to cover the full cost of the service. You charge the CocoWash card for the agreed service amount. During your free trial the full amount is yours to keep. After your trial, CocoWash will apply the 15% platform fee on jobs we brought you. CocoWash will never charge your walk-in customers or interfere with your existing business.

6. VEHICLE CARE RESPONSIBILITY AND INSURANCE
The Service Center Partner accepts full responsibility for every customer vehicle from the time it is delivered by a CocoWash driver until the time it is picked up. This includes damage caused by staff during servicing, damage in the parking lot or facility, theft of the vehicle or contents, and any injury caused to third parties by your staff. CocoWash is not an insurer and assumes no liability for vehicles while in your care. Any claims must be handled through your insurance carrier directly. Failure to maintain required insurance is grounds for immediate removal from the platform.

7. NO EXCLUSIVITY
This agreement does not prevent you from working with other platforms or continuing your existing business. CocoWash only has a revenue share relationship on jobs we bring directly to you.

8. TERMINATION
You may close your account at any time with no penalty. CocoWash may remove a partner for violations of this agreement, failure to maintain insurance, or poor service quality. Outstanding payments will be settled within 5 business days of termination.

9. LIMITATION OF LIABILITY
CocoWash LLC acts solely as a marketplace and logistics coordinator. CocoWash is not responsible for the quality of services performed, damage to vehicles while in your care, or acts of your staff. You agree to indemnify and hold CocoWash LLC harmless from any claims arising from services at your facility.

10. GOVERNING LAW
This agreement is governed by the laws of the State of Maryland. Disputes will be resolved in Montgomery County, Maryland.

Contact: partners@cocowash.org | CocoWash LLC, Montgomery County, Maryland

By clicking I Agree and Create My Account you confirm you are authorized to enter into this agreement and have read and agreed to all terms above.`;

const lS = { display: 'block', fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 6, marginTop: 16 };
const iS = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, color: '#111827', background: '#f9fafb', outline: 'none' };

export default function AuthScreen() {
  const [isLogin, setIsLogin]     = useState(true);
  const [email, setEmail]         = useState('');
  const [password, setPwd]        = useState('');
  const [bizName, setBiz]         = useState('');
  const [agreed, setAgreed]       = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const handle = async () => {
    if (!email || !password) { setError('Email and password are required.'); return; }
    if (!isLogin && !bizName.trim()) { setError('Business name is required.'); return; }
    if (!isLogin && !agreed) { setError('You must agree to the Partner Agreement to create an account.'); return; }
    setLoading(true); setError('');
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      } else {
        const cred = await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
        const now  = new Date();
        const trialEndsAt = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
        await setDoc(doc(db, 'serviceCenters', cred.user.uid), {
          primaryOwnerUid:       cred.user.uid,
          businessName:          bizName.trim(),
          email:                 email.trim().toLowerCase(),
          status:                'approved',
          trialActive:           true,
          trialStartDate:        serverTimestamp(),
          trialEndsAt:           trialEndsAt,
          cocoWashFeePercentage: 15,
          agreedToTerms:         true,
          agreedToTermsAt:       serverTimestamp(),
          createdAt:             serverTimestamp(),
          updatedAt:             serverTimestamp(),
        });
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 40, width: '100%', maxWidth: 460, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 32, fontWeight: 900, color: '#111827' }}>Coco<span style={{ color: '#F97316' }}>Wash</span></div>
          <div style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>Service Center Portal</div>
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 24 }}>{isLogin ? 'Sign In' : 'Create Account'}</h2>
        {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: 12, borderRadius: 10, marginBottom: 16, fontSize: 13 }}>{error}</div>}
        {!isLogin && (
          <>
            <label style={lS}>Business Name</label>
            <input style={iS} value={bizName} onChange={e => setBiz(e.target.value)} placeholder="e.g. Downtown Auto Spa" />
          </>
        )}
        <label style={lS}>Email Address</label>
        <input style={iS} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@business.com" />
        <label style={lS}>Password</label>
        <input style={iS} type="password" value={password} onChange={e => setPwd(e.target.value)} placeholder="••••••••" />

        {!isLogin && (
          <div style={{ marginTop: 20, padding: 16, background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <input
                type="checkbox"
                id="agree"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 2, cursor: 'pointer', accentColor: '#F97316' }}
              />
              <label htmlFor="agree" style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, cursor: 'pointer' }}>
                I have read and agree to the{' '}
                <span
                  onClick={() => setShowTerms(true)}
                  style={{ color: '#F97316', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  CocoWash Service Center Partner Agreement
                </span>
                , including the 90-day free trial terms, the 15% revenue share after trial, garage liability insurance requirements, and vehicle care responsibilities.
              </label>
            </div>
          </div>
        )}

        <button
          onClick={handle}
          disabled={loading || (!isLogin && !agreed)}
          style={{ width: '100%', background: (!isLogin && !agreed) ? '#d1d5db' : '#F97316', color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: (!isLogin && !agreed) ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, marginTop: 16 }}
        >
          {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'I Agree and Create My Account'}
        </button>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#6b7280' }}>
          {isLogin ? "Don't have an account? " : 'Already have an account? '}
          <span style={{ color: '#F97316', cursor: 'pointer', fontWeight: 700 }} onClick={() => { setIsLogin(!isLogin); setError(''); setAgreed(false); }}>
            {isLogin ? 'Sign up' : 'Sign in'}
          </span>
        </div>

        {!isLogin && (
          <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>Your account will be active immediately</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>90 days free — no credit card required</div>
          </div>
        )}
      </div>

      {showTerms && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#111827' }}>Partner Agreement</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>CocoWash Service Center Partner Agreement</div>
              </div>
              <button onClick={() => setShowTerms(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', padding: 24, flex: 1 }}>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, color: '#374151', lineHeight: 1.8 }}>{TERMS}</pre>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 12 }}>
              <button onClick={() => setShowTerms(false)} style={{ flex: 1, padding: '12px', background: '#f3f4f6', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>Close</button>
              <button onClick={() => { setAgreed(true); setShowTerms(false); }} style={{ flex: 1, padding: '12px', background: '#F97316', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#fff' }}>I Agree to These Terms</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}